import { useState, useEffect, useMemo } from 'react';
import { Lock, Send, Download, FileText, Filter, X, ArrowUp, ArrowDown, Layers, FileDown } from 'lucide-react';
import { T, LogoMark as AppLogo, Wordmark, amountStyle } from '../theme';
import GlobalFooterBar from './GlobalFooterBar';
import {
  getReviewLink, verifyPassword,
  addComment, subscribeReviewLink, subscribeComments, getReviewExpenses,
} from '../utils/reviewStorage';
import { formatDate, formatAmount, getCategoryIcon } from '../utils/formatters';
import { downloadBlob } from '../utils/fileHelpers';
import { CATEGORY_KEYS } from '../utils/constants';
import { categoryLabel, normalizeCategory } from '../utils/categoryHelpers';
import PdfExportModal from './PdfExportModal';

// ── Sort / Group helpers (mirrors ExpenseList) ────────────────────────────────

const SORT_OPTIONS = [
  { value: 'date',   label: 'Date' },
  { value: 'amount', label: 'Amount' },
];

const DATE_BASED = new Set(['day', 'week', 'month', 'year']);

const GROUP_LABEL = {
  category: 'Category', location: 'Location',
  day: 'Day', week: 'Week', month: 'Month', year: 'Year',
};

function getWeekStart(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return monday.toISOString().slice(0, 10);
}

function getGroupKey(expense, groupBy) {
  switch (groupBy) {
    case 'category': return normalizeCategory(expense.category) || 'Uncategorized';
    case 'location': return expense.location || 'No Location';
    case 'day':      return expense.date || 'No Date';
    case 'week':     return getWeekStart(expense.date) ?? 'No Date';
    case 'month':    return expense.date ? expense.date.slice(0, 7) : 'No Date';
    case 'year':     return expense.date ? expense.date.slice(0, 4) : 'No Date';
    default:         return '';
  }
}

function formatGroupLabel(key, groupBy) {
  if (!key || key === 'No Date' || key === 'No Location' || key === 'Uncategorized') return key || 'Unknown';
  if (groupBy === 'category') return categoryLabel(key) || key;
  switch (groupBy) {
    case 'day': {
      const d = new Date(key + 'T00:00:00');
      return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    }
    case 'week': {
      const d = new Date(key + 'T00:00:00');
      return 'Week of ' + d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    case 'month': {
      const [y, m] = key.split('-');
      return new Date(+y, +m - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
    }
    default: return key;
  }
}

const EMPTY_FILTERS = {
  category: '', location: '', minAmount: '', maxAmount: '', startDate: '', endDate: '',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  return (
    <div style={{ minHeight: '100vh', background: T.appBg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: T.navy, borderRadius: 16, padding: 32, width: '100%', maxWidth: 360, color: '#fff', textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <Lock size={32} color={T.tealBright} style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: 18, fontWeight: 700 }}>Password Required</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>This report is password protected.</div>
        </div>
        <input
          type="password"
          placeholder="Enter password"
          value={pw}
          onChange={e => { setPw(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && !checking && onUnlock(pw, setError, setChecking)}
          style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${error ? '#f87171' : 'rgba(255,255,255,0.15)'}`, background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 14, boxSizing: 'border-box', marginBottom: 8, outline: 'none' }}
        />
        {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 8 }}>{error}</div>}
        <button
          onClick={() => onUnlock(pw, setError, setChecking)}
          disabled={checking || !pw}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: T.gradTeal, color: '#fff', fontSize: 14, fontWeight: 700, cursor: checking || !pw ? 'not-allowed' : 'pointer', opacity: checking || !pw ? 0.6 : 1 }}
        >
          {checking ? 'Checking…' : 'Unlock'}
        </button>
      </div>
    </div>
  );
}

function ReceiptImage({ expense }) {
  const [expanded, setExpanded] = useState(false);
  const preview = expense.imagePreview;
  if (!preview) return null;
  const isPdf = expense.mimeType === 'application/pdf' || preview.startsWith('data:application/pdf');

  const openFull = () => {
    if (preview.startsWith('data:')) {
      const [header, data] = preview.split(',');
      const mime = header.match(/:(.*?);/)[1];
      const bytes = atob(data);
      const ab = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) ab[i] = bytes.charCodeAt(i);
      window.open(URL.createObjectURL(new Blob([ab], { type: mime })), '_blank');
    } else {
      window.open(preview, '_blank');
    }
  };

  if (isPdf) {
    return (
      <button
        onClick={openFull}
        style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer' }}
      >
        <FileText size={13} /> View PDF
      </button>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <img
        src={preview}
        alt="receipt"
        onClick={() => setExpanded(v => !v)}
        style={{ maxWidth: expanded ? '100%' : 80, maxHeight: expanded ? 'none' : 80, borderRadius: 8, cursor: 'pointer', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', display: 'block' }}
      />
      {!expanded && (
        <button onClick={() => setExpanded(true)} style={{ background: 'none', border: 'none', color: T.tealBright, fontSize: 11, cursor: 'pointer', padding: '2px 0', marginTop: 2 }}>
          Expand
        </button>
      )}
      {expanded && (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
            Collapse
          </button>
          <button onClick={openFull} style={{ background: 'none', border: 'none', color: T.tealBright, fontSize: 11, cursor: 'pointer', padding: 0 }}>
            Open full size
          </button>
        </div>
      )}
    </div>
  );
}

function ExpenseRow({ expense }) {
  const Icon = getCategoryIcon(expense.category);
  const isDining = normalizeCategory(expense.category) === 'food_dining';
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={T.tealBright} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{expense.vendor || 'Unknown vendor'}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                {categoryLabel(expense.category)}{expense.date ? ` · ${formatDate(expense.date)}` : ''}
              </div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', textAlign: 'right', ...amountStyle() }}>
              {expense.location && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{expense.location}</div>}
              {expense.convertedCurrency === expense.currency ? (
                <>
                  {formatAmount(expense.total, expense.currency)}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                    {formatAmount(expense.total, expense.currency)}
                  </div>
                  {formatAmount(expense.convertedTotal, expense.convertedCurrency)}
                </>
              )}
            </div>
          </div>

          {isDining && (expense.mealType || expense.guestName || expense.guestAffiliation) && (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(5,196,176,0.08)', border: '1px solid rgba(5,196,176,0.15)', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {expense.mealType && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.35)' }}>Meal: </span>{expense.mealType}
                </div>
              )}
              {expense.guestName && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.35)' }}>Guest: </span>{expense.guestName}
                  {expense.guestAffiliation && <span style={{ color: 'rgba(255,255,255,0.35)' }}> · {expense.guestAffiliation}</span>}
                </div>
              )}
            </div>
          )}

          {expense.notes && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 6, fontStyle: 'italic' }}>{expense.notes}</div>
          )}
          <ReceiptImage expense={expense} />
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  returned:  { label: 'Returned', color: '#f87171',   bg: 'rgba(248,113,113,0.15)' },
  approved:  { label: 'Approved', color: '#34d399',   bg: 'rgba(52,211,153,0.15)'  },
  paid:      { label: 'Paid',     color: T.tealBright, bg: 'rgba(5,196,176,0.15)'  },
};

const btnStyle = (active) => ({
  padding: '4px 10px', borderRadius: 6,
  border: `1.5px solid ${active ? T.tealBright : 'rgba(255,255,255,0.2)'}`,
  background: active ? T.tealDeep : 'transparent',
  color: active ? T.tealBright : 'rgba(255,255,255,0.55)',
  fontSize: 11, fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 3,
});

function BrandHeader() {
  return (
    <a
      href="/"
      style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
    >
      <AppLogo size={32} />
      <Wordmark size={18} lightColor={T.navy} />
    </a>
  );
}

export default function ReviewPage({ linkId }) {
  const [link, setLink] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [identityError, setIdentityError] = useState('');

  // Sort / filter / group state
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [groupBy, setGroupBy] = useState(null);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Initial load
  useEffect(() => {
    getReviewLink(linkId).then(data => {
      if (!data) { setNotFound(true); setLoading(false); return; }
      setLink(data);
      if (!data.passwordHash) setUnlocked(true);
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [linkId]);

  // Real-time subscriptions (after unlock)
  useEffect(() => {
    if (!unlocked || !link) return;
    const unsubLink = subscribeReviewLink(linkId, data => {
      if (!data) { setNotFound(true); return; }
      setLink(data);
    });
    const unsubComments = subscribeComments(linkId, setComments);
    getReviewExpenses(link.ownerUid, link.fileName).then(setExpenses);
    return () => { unsubLink(); unsubComments(); };
  }, [unlocked, linkId, link?.ownerUid, link?.fileName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlock = async (pw, setError, setChecking) => {
    setChecking(true);
    const ok = await verifyPassword(linkId, pw);
    setChecking(false);
    if (ok) setUnlocked(true);
    else setError('Incorrect password.');
  };

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const filtered = useMemo(() => {
    let result = expenses.filter(e => {
      if (filters.category && normalizeCategory(e.category) !== filters.category) return false;
      if (filters.location && !(e.location || '').toLowerCase().includes(filters.location.toLowerCase())) return false;
      const amt = e.convertedTotal ?? e.total ?? 0;
      if (filters.minAmount !== '' && amt < parseFloat(filters.minAmount)) return false;
      if (filters.maxAmount !== '' && amt > parseFloat(filters.maxAmount)) return false;
      if (filters.startDate && e.date < filters.startDate) return false;
      if (filters.endDate && e.date > filters.endDate) return false;
      return true;
    });
    return [...result].sort((a, b) => {
      const aVal = sortBy === 'amount' ? (a.convertedTotal ?? a.total ?? 0) : (a.date || '');
      const bVal = sortBy === 'amount' ? (b.convertedTotal ?? b.total ?? 0) : (b.date || '');
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expenses, filters, sortBy, sortDir]);

  const filteredSum = useMemo(() => {
    return filtered.reduce((sum, expense) => {
      const amount = expense.convertedTotal ?? expense.total ?? 0;
      return sum + (parseFloat(amount) || 0);
    }, 0);
  }, [filtered]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const e of filtered) {
      const key = getGroupKey(e, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    const entries = [...map.entries()];
    if (DATE_BASED.has(groupBy)) entries.sort((a, b) => b[0].localeCompare(a[0]));
    else entries.sort((a, b) => a[0].localeCompare(b[0]));
    return entries.map(([key, exps]) => ({
      key,
      label: formatGroupLabel(key, groupBy),
      expenses: exps,
      total: exps.reduce((s, e) => s + (parseFloat(e.convertedTotal ?? e.total) || 0), 0),
    }));
  }, [filtered, groupBy]);

  const validateIdentity = () => {
    if (!reviewerName.trim()) { setIdentityError('Please enter your name.'); return false; }
    if (!reviewerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reviewerEmail.trim())) {
      setIdentityError('Please enter a valid email address.');
      return false;
    }
    setIdentityError('');
    return true;
  };

  const handleComment = async () => {
    if (!validateIdentity()) return;
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await addComment(linkId, {
        text: commentText,
        authorName: reviewerName.trim(),
        authorEmail: reviewerEmail.trim(),
        isOwner: false,
      });
      setCommentText('');
    } catch (e) {
      if (e?.code === 'LINK_DELETED') { setNotFound(true); return; }
      throw e;
    } finally {
      setSubmitting(false);
    }
  };
  
  const displayCurrency = expenses[0]?.convertedCurrency || link?.ownerDefaultCurrency || 'USD';
  const total = expenses.reduce((s, e) => s + (parseFloat(e.convertedTotal ?? e.total) || 0), 0);
  const dateRange = useMemo(() => {
    const dates = expenses.map(e => e.date).filter(Boolean).sort();
    if (!dates.length) return null;
    const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return dates[0] === dates[dates.length - 1] ? fmt(dates[0]) : `${fmt(dates[0])} – ${fmt(dates[dates.length - 1])}`;
  }, [expenses]);

  const downloadCSV = () => {
    const headers = ['Date', 'Vendor', 'Category', 'Currency', 'Subtotal', 'Tax', 'Tip', 'Total', 'Report Currency', 'Report Total', 'Meal Type', 'Guest Name', 'Guest Affiliation', 'Notes'];
    const rows = expenses.map(e => [
      e.date || '',
      e.vendor || '',
      categoryLabel(e.category) || e.category || '',
      e.currency || 'USD',
      parseFloat(e.subtotal) || '',
      parseFloat(e.tax) || '',
      parseFloat(e.tip) || '',
      parseFloat(e.total) || 0,
      e.convertedCurrency || e.currency || 'USD',
      parseFloat(e.convertedTotal ?? e.total) || 0,
      e.mealType || '',
      e.guestName || '',
      e.guestAffiliation || '',
      e.notes || '',
    ]);
    const escape = v => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [
      `"# ${link.fileName}"`,
      `"# Generated: ${new Date().toISOString().slice(0, 10)}"`,
      '',
      headers.map(escape).join(','),
      ...rows.map(r => r.map(escape).join(',')),
    ].join('\n');
    downloadBlob(new Blob([csv], { type: 'text/csv' }), `${link.fileName}.csv`);
  };

  // ── Loading / not-found / locked screens ──────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.appBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
        <BrandHeader />
        <div style={{ color: '#9CA3AF', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', background: T.appBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20, gap: 32 }}>
        <BrandHeader />
        <div>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.navy }}>This report is no longer available</div>
          <div style={{ fontSize: 14, color: '#6B7280', marginTop: 8, maxWidth: 320 }}>
            The link has been removed. Please ask the submitter to send you a new link.
          </div>
        </div>
      </div>
    );
  }

  if (!unlocked) return <PasswordGate onUnlock={handleUnlock} />;

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: T.appBg, fontFamily: T.fontBody, paddingBottom: 36 }}>

      {/* Header */}
      <div style={{ background: T.gradNavy, padding: '20px 20px 16px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
                <button
          onClick={() => window.open('/landing', '_blank')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
          title="Home"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AppLogo size={22} />
            <Wordmark size={15} />
          </div>
          </button>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{link.fileName}</div>
          {link.ownerName && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
              Submitted by <span style={{ color: '#fff', fontWeight: 600 }}>{link.ownerName}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', ...amountStyle() }}>
                {formatAmount(total, displayCurrency)} total
              </div>
              {dateRange && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{dateRange}</div>
              )}
              {STATUS_META[link.status] && (
                <div style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, color: STATUS_META[link.status].color, background: STATUS_META[link.status].bg }}>
                  {STATUS_META[link.status].label}{link.reviewerName ? ` · ${link.reviewerName}` : ''}
                </div>
              )}
            </div>
            {expenses.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={downloadCSV}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  <Download size={14} /> CSV
                </button>
                <button
                  onClick={() => setShowPdfModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  <FileDown size={14} /> PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 16px 60px' }}>

        {/* Expense list card — contains sort/filter/group + rows */}
        <div style={{ background: T.navy, borderRadius: 14, margin: '16px 0', overflow: 'hidden' }}>

          {/* Sort / Filter / Group bar */}
          {expenses.length > 0 && (
            <div style={{ padding: '10px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (sortBy === opt.value) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                      else { setSortBy(opt.value); setSortDir('desc'); }
                    }}
                    style={btnStyle(sortBy === opt.value)}
                  >
                    {opt.label}
                    {sortBy === opt.value && (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
                  </button>
                ))}

                <button
                  onClick={() => { setShowFilters(v => !v); setShowGroupPanel(false); }}
                  style={btnStyle(showFilters || activeFilterCount > 0)}
                >
                  <Filter size={10} />
                  Filter
                  {activeFilterCount > 0 && (
                    <span style={{ background: T.tealBright, color: T.navy, borderRadius: '50%', width: 14, height: 14, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => { setShowGroupPanel(v => !v); setShowFilters(false); }}
                  style={btnStyle(showGroupPanel || !!groupBy)}
                >
                  <Layers size={10} />
                  {groupBy ? GROUP_LABEL[groupBy] : 'Group'}
                </button>
              </div>
            </div>
          )}

          {/* Group panel */}
          {showGroupPanel && (
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Group By</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {[
                  { value: null, label: 'None' },
                  { value: 'category', label: 'Category' },
                  { value: 'location', label: 'Location' },
                ].map(opt => (
                  <button
                    key={String(opt.value)}
                    onClick={() => { setGroupBy(opt.value); setShowGroupPanel(false); }}
                    style={{ padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${groupBy === opt.value ? T.tealBright : 'rgba(255,255,255,0.2)'}`, background: groupBy === opt.value ? T.tealDeep : 'transparent', color: groupBy === opt.value ? T.tealBright : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>By Date</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { value: 'day', label: 'Day' },
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                  { value: 'year', label: 'Year' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setGroupBy(opt.value); setShowGroupPanel(false); }}
                    style={{ padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${groupBy === opt.value ? T.tealBright : 'rgba(255,255,255,0.2)'}`, background: groupBy === opt.value ? T.tealDeep : 'transparent', color: groupBy === opt.value ? T.tealBright : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filter panel */}
          {showFilters && (
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>Filters</span>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: T.tealBright, fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    <X size={11} /> Clear all
                  </button>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={filterLabel}>Category</label>
                <select value={filters.category} onChange={e => setFilter('category', e.target.value)} style={filterInput}>
                  <option value="">All categories</option>
                  {Object.entries(CATEGORY_KEYS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={filterLabel}>Location</label>
                <input type="text" placeholder="Search location…" value={filters.location} onChange={e => setFilter('location', e.target.value)} style={filterInput} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={filterLabel}>Amount</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="number" placeholder="Min" value={filters.minAmount} onChange={e => setFilter('minAmount', e.target.value)} style={{ ...filterInput, flex: 1 }} min="0" />
                  <input type="number" placeholder="Max" value={filters.maxAmount} onChange={e => setFilter('maxAmount', e.target.value)} style={{ ...filterInput, flex: 1 }} min="0" />
                </div>
              </div>
              <div>
                <label style={filterLabel}>Date Range</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="date" value={filters.startDate} onChange={e => setFilter('startDate', e.target.value)} style={{ ...filterInput, flex: 1 }} />
                  <input type="date" value={filters.endDate} onChange={e => setFilter('endDate', e.target.value)} style={{ ...filterInput, flex: 1 }} />
                </div>
              </div>
            </div>
          )}

          {activeFilterCount > 0 && (
            <div style={{ padding: '6px 16px', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              {/* {filtered.length} result{filtered.length !== 1 ? 's' : ''} */}
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} · {displayCurrency || 'USD'} {filteredSum.toFixed(2)}

            </div>
          )}

          {/* Expense rows */}
          <div style={{ padding: '0 16px' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
                {activeFilterCount > 0 ? 'No matching expenses' : 'No expenses'}
              </div>
            ) : grouped ? (
              grouped.map(group => (
                <div key={group.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{group.label}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 600, ...amountStyle() }}>
                      {group.expenses.length} · {formatAmount(group.total, displayCurrency)}
                    </span>
                  </div>
                  {group.expenses.map((e, i) => <ExpenseRow key={e.id || i} expense={e} />)}
                </div>
              ))
            ) : (
              filtered.map((e, i) => <ExpenseRow key={e.id || i} expense={e} />)
            )}
          </div>
        </div>

        {/* Comments/Chat */}
        <div style={{ background: T.navy, borderRadius: 14, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 16 }}>
            Comments/Chat {comments.length > 0 && `(${comments.length})`}
          </div>

          {comments.length > 0 && (
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {comments.map(c => (
                <div key={c.id} style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: '50%', background: c.isOwner ? T.tealDeep : 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: c.isOwner ? T.tealBright : '#fff' }}>
                    {(c.authorName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: c.isOwner ? T.tealBright : 'rgba(255,255,255,0.7)', marginBottom: 2 }}>
                      {c.authorName || 'Anonymous'}{c.isOwner ? ' (Submitter)' : ''}
                    </div>
                    {c.authorEmail && !c.isOwner && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>{c.authorEmail}</div>
                    )}
                    <div style={{ fontSize: 14, color: '#fff', lineHeight: 1.4 }}>{c.text}</div>
                    {c.createdAt?.toDate && (
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 6 }}>
                        {c.createdAt.toDate().toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Identity fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder="Your name *"
                value={reviewerName}
                onChange={e => { setReviewerName(e.target.value); setIdentityError(''); }}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${identityError && !reviewerName.trim() ? '#f87171' : 'rgba(255,255,255,0.15)'}`, background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 13, outline: 'none' }}
              />
              <input
                type="email"
                placeholder="Your email *"
                value={reviewerEmail}
                onChange={e => { setReviewerEmail(e.target.value); setIdentityError(''); }}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${identityError && !reviewerEmail.trim() ? '#f87171' : 'rgba(255,255,255,0.15)'}`, background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 13, outline: 'none' }}
              />
            </div>
            {identityError && <div style={{ fontSize: 12, color: '#f87171' }}>{identityError}</div>}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <textarea
              placeholder="Add a comment…"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              rows={2}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: '#fff', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: T.fontBody }}
            />
            <button
              onClick={handleComment}
              disabled={submitting || !commentText.trim()}
              style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: T.gradTeal, color: '#fff', cursor: submitting || !commentText.trim() ? 'not-allowed' : 'pointer', opacity: submitting || !commentText.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center' }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>

      </div>

      {showPdfModal && (
        <PdfExportModal
          expenses={expenses}
          reportName={link.fileName}
          ownerName={link.ownerName}
          dateRange={dateRange}
          defaultCurrency={link.ownerDefaultCurrency || 'USD'}
          onClose={() => setShowPdfModal(false)}
        />
      )}
      <GlobalFooterBar />
    </div>
  );
}

const filterLabel = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
};

const filterInput = {
  width: '100%', padding: '7px 10px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.15)', fontSize: 13, color: '#fff',
  background: 'rgba(255,255,255,0.07)', boxSizing: 'border-box', outline: 'none',
  colorScheme: 'dark',
};
